import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { qtyDisplay } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, RefreshCw, ChevronRight, FileText } from "lucide-react";

const TABS = [
  { key: "pending", label: "Pending", match: ["pending_vet_review", "under_vet_review"] },
  { key: "approved", label: "Approved", match: ["vet_approved", "sent_to_fulfillment", "processing", "shipped", "delivered", "completed"] },
  { key: "rejected", label: "Rejected", match: ["vet_rejected"] },
  { key: "modification", label: "Modification Requested", match: ["vet_modification_requested"] },
];

const STATUS_BADGE = {
  pending_vet_review: "bg-amber-500/15 text-amber-300 border-amber-500/20",
  under_vet_review: "bg-violet-500/15 text-violet-300 border-violet-500/20",
  vet_approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
  vet_rejected: "bg-red-500/15 text-red-300 border-red-500/20",
  vet_modification_requested: "bg-yellow-500/15 text-yellow-300 border-yellow-500/20",
  sent_to_fulfillment: "bg-sky-500/15 text-sky-300 border-sky-500/20",
  processing: "bg-amber-500/15 text-amber-300 border-amber-500/20",
  shipped: "bg-cyan-500/15 text-cyan-300 border-cyan-500/20",
  delivered: "bg-green-500/15 text-green-300 border-green-500/20",
  completed: "bg-green-500/15 text-green-300 border-green-500/20",
};

export default function VetDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/vet/orders"); setOrders(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = {};
    for (const t of TABS) c[t.key] = orders.filter((o) => t.match.includes(o.status)).length;
    return c;
  }, [orders]);

  const visible = orders.filter((o) => TABS.find((t) => t.key === tab)?.match.includes(o.status));

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="vet-dashboard">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-white/40 mb-2">Queue</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Prescriptions</h1>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 font-manrope text-sm transition-colors" data-testid="vet-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="vet-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 h-9 rounded-lg text-sm font-manrope transition-colors ${tab === t.key ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"}`} data-testid={`vet-tab-${t.key}`}>
            {t.label} <span className="text-white/40 ml-1">{counts[t.key] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-[#142318] border border-white/5 rounded-2xl p-12 text-center font-manrope text-white/40">
          <FileText className="w-8 h-8 mx-auto mb-3 text-white/20" strokeWidth={1.5} />
          Nothing in this tab right now.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((o) => (
            <Link key={o.order_id} to={`/vet/order/${o.order_id}`} className="bg-[#142318] border border-white/5 hover:border-emerald-500/30 rounded-2xl p-5 transition-colors group" data-testid={`vet-order-${o.order_id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-outfit font-semibold text-sm">{o.medication?.name || "Medication"}</p>
                  <p className="font-manrope text-xs text-white/40 mt-0.5">{o.medication?.dosage} · Quantity {qtyDisplay(o.medication)}</p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${STATUS_BADGE[o.status] || "bg-white/5 text-white/40 border-white/10"}`}>{o.status}</span>
              </div>
              <div className="border-t border-white/5 pt-3 flex justify-between items-center">
                <div>
                  <p className="font-manrope text-sm">{o.pet?.name || "n/a"} <span className="text-white/40">({o.pet?.species || "?"})</span></p>
                  <p className="font-manrope text-xs text-white/40">Order {o.order_id?.slice(0, 8)}…</p>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-emerald-400 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
